import { useEffect, useState, useCallback, useRef } from "react";
import { useLocation, useSearchParams } from "react-router";
import { observer } from "mobx-react-lite";
import {
  Box,
  Snackbar,
  Alert,
  SwipeableDrawer,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import { useClaimsStore } from "../hooks/useClaimsStore";
import { useAppStore } from "../hooks/useAppStore";
import { useChannels } from "../contexts/ChannelsContext";
import Column from "../components/ui/core/Column";
import Row from "../components/ui/core/Row";
import ClaimsFilters from "../components/claims/ClaimsFilters";
import ClaimsTable from "../components/claims/ClaimsTable";
import ClaimPreview from "../components/claims/ClaimPreview";
import ClaimsDropZone from "../components/claims/ClaimsDropZone";
import ClaimPredefaultQuestionPreview from "../components/claims/components/ClaimPredefaultQuestionPreview";
import { ClaimsUploadResult, createPlaceholderClaim } from "../types/claims";
import { useChatCategoryStore } from "../hooks/useChatCategoryStore";
import { useChatStore } from "../hooks/useChatStore";
import { useMainStore } from "../hooks/useMainStore";
import { useCapabilities } from "../contexts/permissions/useCapabilities";
import { clearPersistedClaimFilter } from "../components/helpers/claimFilterStorage";

interface SnackbarState {
  open: boolean;
  message: string;
  severity: "success" | "error" | "info" | "warning";
}

const Claims = observer(() => {
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const {
    clearClaimsStore,
    loadClaimsFilterOptions,
    loadClaimsData,
    claimToPreview,
    setClaimToPreview,
    tempClaimNumber,
    setTempClaimNumber,
    triggerPreviewRefresh,
    setClaimKeywordSearch,
    claimActivePredefaultQuestion,
  } = useClaimsStore();
  const { sidebarWidth } = useAppStore();
  const chatStore = useChatStore();
  const { filtersScope } = useMainStore();
  const { setFilterByClaimId } = useChannels();
  const containerRef = useRef<HTMLDivElement>(null);
  const positionRef = useRef<"left" | "right">("right");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("lg"));
  const { showSidebar } = useCapabilities();

  const [isUrlClaimChecked, setIsUrlClaimChecked] = useState(false);

  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: "",
    severity: "success",
  });
  const [viewerPosition, setViewerPosition] = useState<"left" | "right">(
    "right",
  );

  const { sidePanelWidth } = useChatCategoryStore();

  // Update position ref when viewerPosition changes
  useEffect(() => {
    positionRef.current = viewerPosition;
  }, [viewerPosition]);

  useEffect(() => {
    loadClaimsFilterOptions();
    if (!tempClaimNumber) {
      setClaimToPreview(null);
    }

    return () => {
      clearClaimsStore();
    };
  }, []);

  // Open claim preview from ?claim_number= URL or tempClaimNumber (e.g. Sidebar redirect after delete).
  const openingClaimRef = useRef<string | null>(null);

  useEffect(() => {
    const claimNumberFromUrl = new URLSearchParams(location.search).get(
      "claim_number",
    );
    const claimNumber = claimNumberFromUrl ?? tempClaimNumber;
    if (!claimNumber) {
      setIsUrlClaimChecked(true);
      return;
    }

    if (openingClaimRef.current === claimNumber) {
      setIsUrlClaimChecked(true);
      return;
    }

    openingClaimRef.current = claimNumber;

    if (claimNumberFromUrl) {
      // Will trigger load claims data
      setClaimKeywordSearch(claimNumberFromUrl);
    }

    setIsUrlClaimChecked(true);
    chatStore.setIsClaimDataLoading(true);
    setClaimToPreview(createPlaceholderClaim(claimNumber));

    let cancelled = false;
    chatStore.loadClaimDetail(claimNumber).then((data) => {
      if (!cancelled) {
        if (data?.claim) {
          setClaimToPreview(data.claim);
        }
        setTempClaimNumber(null);
        if (openingClaimRef.current === claimNumber) {
          openingClaimRef.current = null;
        }
      }

      // Strip claim_number from the URL only after the fetch settles, so
      // clearing it doesn't change location.search (an effect dependency)
      // and retrigger this effect mid-flight, cancelling itself.
      if (claimNumberFromUrl) {
        setSearchParams(
          (prev) => {
            if (!prev.has("claim_number")) return prev;
            const next = new URLSearchParams(prev);
            next.delete("claim_number");
            return next;
          },
          { replace: true },
        );
      }
    });

    return () => {
      cancelled = true;
      if (openingClaimRef.current === claimNumber) {
        openingClaimRef.current = null;
      }
    };
    // setSearchParams/setters/chatStore are stable; openingClaimRef avoids duplicate opens after URL clear.
  }, [location.search, tempClaimNumber]);

  const handleUploadComplete = useCallback(
    (result: ClaimsUploadResult) => {
      const totalAffected = result.claims.length;
      const duplicateCount = result.duplicates?.length || 0;
      const invalidPcnCount = result.invalid_pcns?.length || 0;

      // Build the appropriate message
      let message = "";
      let severity: "success" | "warning" | "error" = "success";

      // Determine the appropriate message
      if (duplicateCount > 0 && totalAffected === 0 && invalidPcnCount === 0) {
        // All files were duplicates
        const fileNames = result.duplicates.map((d) => d.file).join(", ");
        setSnackbar({
          open: true,
          message: `File${duplicateCount > 1 ? "s" : ""} already uploaded: ${fileNames}`,
          severity: "warning",
        });
        return;
      }

      // Base message
      if (totalAffected > 0) {
        message = `Successfully processed ${totalAffected} claim${totalAffected !== 1 ? "s" : ""}`;
      }

      // Add duplicate info
      if (duplicateCount > 0) {
        message += message
          ? `, ${duplicateCount} duplicate${duplicateCount !== 1 ? "s" : ""} skipped`
          : `${duplicateCount} duplicate${duplicateCount !== 1 ? "s" : ""} skipped`;
      }

      // Add invalid PCN warning
      if (invalidPcnCount > 0) {
        severity = "warning";
        const pcnWarning = `${invalidPcnCount} invalid PCN${invalidPcnCount !== 1 ? "s" : ""} skipped`;
        message += message ? `. ${pcnWarning}` : pcnWarning;
      }

      if (message) {
        setSnackbar({
          open: true,
          message,
          severity,
        });
      }

      // Reload claims data to ensure table shows latest merged data (837 + 835)
      // This is necessary because 835 files merge into existing 837 claims,
      // and we need to refresh to see the updated status/amounts
      loadClaimsData();
      // If the currently-previewed claim was affected, refresh the preview
      if (claimToPreview && result.claim_ids.includes(claimToPreview.id)) {
        triggerPreviewRefresh();
      }
    },
    [loadClaimsData, claimToPreview, triggerPreviewRefresh],
  );

  const handleUploadError = useCallback((error: string) => {
    setSnackbar({
      open: true,
      message: error,
      severity: "error",
    });
  }, []);

  const handleSnackbarClose = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  // Calculate the main content width based on what's shown on the right
  const getMainContentWidth = () => {
    if (claimToPreview) {
      return `calc(100% - ${sidePanelWidth}%)`;
    }
    return "100%";
  };

  // Calculate available width (viewport - sidebar)
  const availableWidth = isMobile || !showSidebar ? "100%" : `calc(100vw - ${sidebarWidth}px)`;

  // Non expensive check to avoid rendering the component which calls loadClaimsData twice (on setKeywordSearch)
  if (!isUrlClaimChecked) {
    return null;
  }

  return (
    <Row
      id="chat-container-ref"
      ref={containerRef}
      sx={{
        height: "100%",
        overflow: "hidden",
        width: availableWidth,
        flexDirection: viewerPosition === "left" ? "row-reverse" : "row",
      }}
    >
      <Column
        sx={{
          gap: { xs: 1, sm: 1, md: 1, lg: 1.5 },
          backgroundColor: "secondary.main",
          width: getMainContentWidth(),
          minWidth: 0,
          overflow: "auto",
          flexShrink: 0,
        }}
      >
        {/* Keep ClaimsTable mounted while the predefault preview shows — unmounting
            it (a plain ternary) re-triggers its full loadClaimsData() on every
            open/cancel, refetching page 0 and dropping virtualized scroll state. */}
        <Box sx={{ display: claimActivePredefaultQuestion ? "none" : "contents" }}>
          <ClaimsDropZone
            onUploadComplete={handleUploadComplete}
            onUploadError={handleUploadError}
          >
            <Column>
              <ClaimsFilters />

              {/* The summary is the table's footer row -- it lives inside
                  ClaimsTable, under the rows it totals. */}
              <ClaimsTable selectTableRowCallback={setFilterByClaimId} />
            </Column>
          </ClaimsDropZone>
        </Box>

        {claimActivePredefaultQuestion ? <ClaimPredefaultQuestionPreview /> : null}
      </Column>

      {!isMobileOrTablet && claimToPreview ? (
        <ClaimPreview
          viewerPosition={viewerPosition}
          onTogglePosition={() =>
            setViewerPosition(viewerPosition === "left" ? "right" : "left")
          }
          onCloseCallback={() => {
            setFilterByClaimId(null);
            // Closing the preview is an explicit "done with this claim", unlike
            // the routine null resets Sidebar deliberately doesn't persist — so
            // drop the stored filter here, or a refresh would restore it and
            // ClaimsTable would reopen the preview the user just closed.
            clearPersistedClaimFilter(filtersScope);
          }}
        />
      ) : null}

      {/* Mobile/Tablet: SwipeableDrawer from right */}
      {!isMobileOrTablet ? null : (
        <SwipeableDrawer
          anchor="right"
          open={isMobileOrTablet && !!claimToPreview}
          onClose={() => setClaimToPreview(null)}
          onOpen={() => {}}
          sx={{
            "& .MuiDrawer-paper": {
              width: "100%",
              maxWidth: "100vw",
              boxSizing: "border-box",
            },
          }}
          ModalProps={{
            keepMounted: true,
          }}
        >
          <ClaimPreview />
        </SwipeableDrawer>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Row>
  );
});

export default Claims;
